'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbItem {
  label: string;
  path: string;
}

interface NavigationBreadcrumbProps {
  customItems?: BreadcrumbItem[];
  className?: string;
}

const NavigationBreadcrumb = ({ customItems, className = '' }: NavigationBreadcrumbProps) => {
  const pathname = usePathname();

  const routeLabels: Record<string, string> = {
    '/user-dashboard': 'Dashboard',
    '/ai-business-coach': 'AI Business Coach',
    '/account-settings': 'Account Settings',
    '/feedback-collection': 'Feedback',
    '/landing-page': 'Home',
  };

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (customItems) return customItems;

    const paths = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: 'Home', path: '/user-dashboard' }
    ];

    let currentPath = '';
    paths.forEach((path) => {
      currentPath += `/${path}`;
      const label = routeLabels[currentPath] || path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
      breadcrumbs.push({ label, path: currentPath });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs.length <= 1) return null;

  return (
    <nav aria-label="Breadcrumb" className={`flex items-center space-x-2 py-4 ${className}`}>
      {breadcrumbs.map((item, index) => {
        const isLast = index === breadcrumbs.length - 1;
        
        return (
          <React.Fragment key={item.path}>
            {index > 0 && (
              <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
            )}
            {isLast ? (
              <span className="caption text-foreground font-medium" aria-current="page">
                {item.label}
              </span>
            ) : (
              <Link
                href={item.path}
                className="caption text-muted-foreground hover:text-primary transition-smooth"
              >
                {item.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};

export default NavigationBreadcrumb;